// @ts-check
// Controles dos painéis de propriedades e de estilo do editor de e-mail (spec/email.md seção 19.2): cor (com "Cor da
// escola"), controle deslizante, escolha em botões, texto, margens e endereço com variável.
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { normalizeEmailColor } from '../../../../shared/email-design/email-document.js';
import { isValidDocumentLinkUrl } from '../../../../shared/email-design/email-links.js';
import { Icon } from '../../ui/icon.js';
import { LightIconButton } from '../../ui/button.js';
import { Toggle } from '../../ui/feedback.js';
import { VariablePicker } from './variable-picker.js';
import { t } from '../../i18n/index.js';

let fieldSequence = 0;
/** Identificador estável por instância, para ligar rótulo e campo. @param {string} prefix */
export function useFieldId(prefix) {
  const idRef = useRef('');
  if (!idRef.current) {
    fieldSequence += 1;
    idRef.current = `${prefix}-${fieldSequence}`;
  }
  return idRef.current;
}

/**
 * @param {{ label: string, children: any, hint?: string, error?: string | null, htmlFor?: string }} props
 */
export function PropertyRow({ label, children, hint, error, htmlFor }) {
  return html`<div class="email-property-row">
    <label class="email-property-label" for=${htmlFor}>${label}</label>
    ${children}
    ${error ? html`<span class="input-hint" data-danger="true">${error}</span>` : hint ? html`<span class="input-hint">${hint}</span>` : null}
  </div>`;
}

/**
 * Cor #RRGGBB, "Cor da escola" (BRAND) ou nenhuma (null, quando `allowNone`).
 * @param {{ label: string, value: string | null, onChange: (value: string | null) => void, brandColor?: string, allowBrand?: boolean,
 *   allowNone?: boolean, disabled?: boolean, hint?: string }} props
 */
export function ColorField({ label, value, onChange, brandColor = '#1961ed', allowBrand = false, allowNone = false, disabled = false, hint }) {
  const id = useFieldId('email-color');
  const isBrand = value === 'BRAND';
  const shown = isBrand ? brandColor : value ?? '#ffffff';
  const [draft, setDraft] = useState(/** @type {string | null} */ (null));
  const commit = (/** @type {string} */ text) => {
    const color = normalizeEmailColor(text);
    setDraft(null);
    if (color) onChange(color);
  };
  return html`<${PropertyRow} label=${label} htmlFor=${id} hint=${hint}>
    <div class="email-color-field" data-disabled=${disabled ? 'true' : undefined}>
      <input type="color" class="email-color-input" value=${normalizeEmailColor(shown) ?? '#ffffff'} disabled=${disabled || isBrand}
        aria-label=${label} onInput=${(/** @type {Event} */ event) => onChange(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
      <input id=${id} class="text-input email-color-text" value=${draft ?? (isBrand ? t('emailDesign.properties.brandColor') : value ?? '')}
        placeholder=${allowNone ? t('emailDesign.properties.noColor') : '#000000'} disabled=${disabled || isBrand} maxLength=${7}
        onInput=${(/** @type {Event} */ event) => setDraft(/** @type {HTMLInputElement} */ (event.currentTarget).value)}
        onBlur=${(/** @type {Event} */ event) => {
          const text = /** @type {HTMLInputElement} */ (event.currentTarget).value.trim();
          if (allowNone && text === '') {
            setDraft(null);
            onChange(null);
          } else commit(text);
        }}
        onKeyDown=${(/** @type {KeyboardEvent} */ event) => event.key === 'Enter' && /** @type {HTMLInputElement} */ (event.currentTarget).blur()} />
      ${allowNone && value && !isBrand ? html`<${LightIconButton} icon="IconX" ariaLabel=${t('emailDesign.properties.noColor')} disabled=${disabled} onClick=${() => onChange(null)} />` : null}
    </div>
    ${allowBrand
      ? html`<label class="email-inline-toggle">
          <${Toggle} size="small" value=${isBrand} disabled=${disabled} ariaLabel=${t('emailDesign.properties.useBrandColor')}
            onChange=${(/** @type {boolean} */ next) => onChange(next ? 'BRAND' : normalizeEmailColor(brandColor) ?? '#1961ed')} />
          <span>${t('emailDesign.properties.useBrandColor')}</span>
        </label>`
      : null}
  <//>`;
}

/**
 * @param {{ label: string, value: number, min: number, max: number, step?: number, unit?: string, onChange: (value: number) => void, disabled?: boolean }} props
 */
export function RangeField({ label, value, min, max, step = 1, unit = 'px', onChange, disabled = false }) {
  const id = useFieldId('email-range');
  const clamp = (/** @type {number} */ number) => Math.min(max, Math.max(min, Math.round(number)));
  return html`<${PropertyRow} label=${label} htmlFor=${id}>
    <div class="email-range-field">
      <input id=${id} type="range" min=${min} max=${max} step=${step} value=${value} disabled=${disabled}
        onInput=${(/** @type {Event} */ event) => onChange(clamp(Number(/** @type {HTMLInputElement} */ (event.currentTarget).value)))} />
      <span class="email-range-value">
        <input class="text-input" type="number" min=${min} max=${max} value=${value} disabled=${disabled} aria-label=${label}
          onChange=${(/** @type {Event} */ event) => {
            const number = Number(/** @type {HTMLInputElement} */ (event.currentTarget).value);
            if (Number.isFinite(number)) onChange(clamp(number));
          }} />
        <span>${unit}</span>
      </span>
    </div>
  <//>`;
}

/**
 * @template {string} TValue
 * @param {{ label: string, value: TValue, options: Array<{ value: TValue, label: string, icon?: string }>, onChange: (value: TValue) => void,
 *   disabled?: boolean, iconOnly?: boolean }} props
 */
export function ChoiceField({ label, value, options, onChange, disabled = false, iconOnly = false }) {
  return html`<${PropertyRow} label=${label}>
    <div class="email-choice-field" role="radiogroup" aria-label=${label}>
      ${options.map((option) => html`<button
        type="button"
        role="radio"
        class="email-choice-option"
        aria-checked=${option.value === value ? 'true' : 'false'}
        aria-label=${option.label}
        title=${option.label}
        disabled=${disabled}
        onClick=${() => onChange(option.value)}
      >
        ${option.icon ? html`<${Icon} name=${option.icon} size=${16} />` : null}
        ${iconOnly && option.icon ? null : html`<span>${option.label}</span>`}
      </button>`)}
    </div>
  <//>`;
}

/**
 * @param {{ label: string, value: string, onChange: (value: string) => void, placeholder?: string, maxLength?: number, hint?: string,
 *   error?: string | null, multiline?: boolean, monospace?: boolean, disabled?: boolean, onBlur?: () => void, rows?: number }} props
 */
export function TextField({ label, value, onChange, placeholder, maxLength, hint, error, multiline = false, monospace = false, disabled = false, onBlur, rows = 4 }) {
  const id = useFieldId('email-text');
  /** @param {Event} event */
  const handleInput = (event) => onChange(/** @type {HTMLInputElement} */ (event.currentTarget).value);
  return html`<${PropertyRow} label=${label} htmlFor=${id} hint=${hint} error=${error}>
    ${multiline
      ? html`<textarea id=${id} class=${`text-input email-textarea ${monospace ? 'email-code-input' : ''}`} rows=${rows} value=${value} placeholder=${placeholder}
          maxLength=${maxLength} disabled=${disabled} spellcheck=${monospace ? 'false' : undefined} data-error=${error ? 'true' : undefined}
          onInput=${handleInput} onBlur=${onBlur}></textarea>`
      : html`<input id=${id} class="text-input" value=${value} placeholder=${placeholder} maxLength=${maxLength} disabled=${disabled}
          data-error=${error ? 'true' : undefined} onInput=${handleInput} onBlur=${onBlur} />`}
  <//>`;
}

/**
 * Margens internas: em cima, à direita, embaixo e à esquerda.
 * @param {{ label: string, value: { top: number, right: number, bottom: number, left: number }, onChange: (value: { top: number, right: number, bottom: number, left: number }) => void,
 *   disabled?: boolean }} props
 */
export function PaddingField({ label, value, onChange, disabled = false }) {
  const sides = /** @type {const} */ (['top', 'right', 'bottom', 'left']);
  return html`<${PropertyRow} label=${label}>
    <div class="email-padding-field">
      ${sides.map((side) => html`<label class="email-padding-side">
        <span>${t(`emailDesign.properties.sides.${side}`)}</span>
        <input class="text-input" type="number" min="0" max="80" value=${value[side]} disabled=${disabled}
          onChange=${(/** @type {Event} */ event) => {
            const number = Math.min(80, Math.max(0, Math.round(Number(/** @type {HTMLInputElement} */ (event.currentTarget).value) || 0)));
            onChange({ ...value, [side]: number });
          }} />
      </label>`)}
    </div>
  <//>`;
}

/**
 * Endereço de link (https, mailto, tel ou variável), com "Inserir variável".
 * @param {{ label: string, value: string, onChange: (value: string) => void, allowEmpty?: boolean, disabled?: boolean,
 *   catalog: import('../../data/email-templates.js').EmailVariableCatalog | null }} props
 */
export function UrlField({ label, value, onChange, allowEmpty = true, disabled = false, catalog }) {
  const id = useFieldId('email-url');
  const anchorRef = useRef(/** @type {HTMLElement | null} */ (null));
  const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const trimmed = value.trim();
  const isValid = trimmed === '' ? allowEmpty : isValidDocumentLinkUrl(trimmed, { allowMailto: true });
  /** @param {string} expression */
  const insert = (expression) => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;
    onChange(`${value.slice(0, start)}${expression}${value.slice(end)}`);
  };
  return html`<${PropertyRow} label=${label} htmlFor=${id}
    hint=${t('emailDesign.properties.linkHint')} error=${isValid ? null : trimmed === '' ? t('emailDesign.properties.linkRequired') : t('emailDesign.properties.linkInvalid')}>
    <div class="email-url-field" ref=${anchorRef}>
      <input id=${id} ref=${inputRef} class="text-input" value=${value} placeholder="https://" disabled=${disabled} data-error=${isValid ? undefined : 'true'}
        onInput=${(/** @type {Event} */ event) => onChange(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
      <${LightIconButton} icon="IconBraces" ariaLabel=${t('emailDesign.toolbar.variable')} disabled=${disabled} onClick=${() => setIsPickerOpen((open) => !open)} />
    </div>
    <${VariablePicker} anchorRef=${anchorRef} isOpen=${isPickerOpen} onClose=${() => setIsPickerOpen(false)} catalog=${catalog} placement="bottom-end"
      onSelect=${(/** @type {string} */ expression) => insert(expression)} />
  <//>`;
}

/**
 * Interruptor com rótulo, numa linha.
 * @param {{ label: string, value: boolean, onChange: (value: boolean) => void, disabled?: boolean, hint?: string }} props
 */
export function ToggleField({ label, value, onChange, disabled = false, hint }) {
  return html`<div class="email-property-row">
    <label class="email-inline-toggle">
      <${Toggle} size="small" value=${value} onChange=${onChange} disabled=${disabled} ariaLabel=${label} />
      <span>${label}</span>
    </label>
    ${hint ? html`<span class="input-hint">${hint}</span>` : null}
  </div>`;
}
